/**
 * Mirrors Delivered — theme-side CRO instrumentation.
 *
 * The Shopify web pixel covers standard funnel events but runs in a sandbox
 * with NO DOM access — it cannot see the mirror builder's steps, the ZIP
 * checker, or any custom UI. This file is the theme-side companion: it
 * exposes `window.mdCroTrack()` so theme JS can log custom events to the
 * same pipeline (dispatch /api/public/cro-events → Supabase cro_events).
 *
 * Loaded from layout/theme.liquid with `defer`. Consumed by
 * assets/modal-mirror-configurator.js (builder step/complete/error events).
 *
 * Canonical source + runbook live in the dispatch repo:
 * docs/shopify-snippets/cro/ — keep the two copies in sync.
 *
 * NOTE on session ids: this runs on the real page, the pixel in a sandbox —
 * their per-tab session ids are generated independently, so builder events
 * and pixel events may carry different session_ids. Builder funnels are
 * self-consistent (all steps share the page's id); cross-joins to the pixel
 * funnel happen at the day level in the rollup, which is what /cro charts.
 */
;(function () {
  'use strict'

  var ENDPOINT = 'https://mirrors-dispatch.vercel.app/api/public/cro-events'

  function device() {
    var ua = navigator.userAgent || ''
    if (/iPad|Tablet/i.test(ua)) return 'tablet'
    if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile'
    return 'desktop'
  }

  function sid() {
    try {
      var s = sessionStorage.getItem('md_sid')
      if (!s) {
        s = 's_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
        sessionStorage.setItem('md_sid', s)
      }
      return s
    } catch (e) {
      return null
    }
  }

  function utm() {
    try {
      var stored = sessionStorage.getItem('md_utm')
      if (stored) return JSON.parse(stored)
      var p = new URLSearchParams(location.search)
      if (p.get('utm_source')) {
        var u = {
          utm_source: p.get('utm_source'),
          utm_medium: p.get('utm_medium'),
          utm_campaign: p.get('utm_campaign'),
        }
        sessionStorage.setItem('md_utm', JSON.stringify(u))
        return u
      }
    } catch (e) {}
    return {}
  }

  /**
   * window.mdCroTrack(event, extra)
   *   event — one of the md_* names allowlisted by the endpoint:
   *           md_builder_step | md_builder_complete | md_zip_check |
   *           md_cta_click | md_section_view | md_error
   *   extra — { detail: {...}, zip, value, product_handle, ... } (optional)
   */
  window.mdCroTrack = function (event, extra) {
    try {
      extra = extra || {}
      // Convenience: fold top-level step/name into detail — the endpoint
      // only stores known top-level fields, and the builder-funnel rollup
      // reads detail->>'step'.
      if (extra.step != null || extra.name != null) {
        var d = Object.assign({}, extra.detail || {})
        if (extra.step != null && d.step == null) d.step = String(extra.step)
        if (extra.name != null && d.name == null) d.name = extra.name
        extra = Object.assign({}, extra, { detail: d })
        delete extra.step
        delete extra.name
      }
      var payload = Object.assign(
        {
          event: event,
          ts: Date.now(),
          session_id: sid(),
          device: device(),
          page_path: location.pathname,
          referrer: document.referrer || null,
        },
        utm(),
        extra,
      )
      // Mirror into Clarity as a custom event so recordings are filterable.
      try {
        if (typeof window.clarity === 'function') window.clarity('event', event)
      } catch (e) {}
      var body = JSON.stringify(payload)
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, body)
      } else {
        fetch(ENDPOINT, { method: 'POST', body: body, keepalive: true }).catch(function () {})
      }
    } catch (e) {
      /* analytics must never break shopping */
    }
  }

  /** ZIP check helper — fires the event AND tags the Clarity session. */
  window.mdCroCheckZip = function (zip, serviceable) {
    window.mdCroTrack('md_zip_check', { zip: zip, detail: { serviceable: serviceable != null ? !!serviceable : null } })
    try {
      if (typeof window.clarity === 'function' && serviceable != null) {
        window.clarity('set', 'in_market', serviceable ? 'true' : 'false')
      }
    } catch (e) {}
  }

  // Zero-code instrumentation: clicks on [data-md-cro] elements.
  document.addEventListener(
    'click',
    function (ev) {
      try {
        var el = ev.target && ev.target.closest ? ev.target.closest('[data-md-cro]') : null
        if (!el) return
        var id = el.getAttribute('data-md-cro') || ''
        window.mdCroTrack('md_cta_click', { detail: { id: id } })
        var m = id.match(/^builder-(?:next|step)-(\d+)$/)
        if (m) window.mdCroTrack('md_builder_step', { detail: { step: m[1] } })
      } catch (e) {}
    },
    { capture: true, passive: true },
  )

  // Surface storefront JS errors as friction signals (sampled: first 3/page).
  var errCount = 0
  window.addEventListener('error', function (ev) {
    if (errCount++ >= 3) return
    try {
      window.mdCroTrack('md_error', {
        detail: { message: String(ev.message || '').slice(0, 200), source: String(ev.filename || '').slice(0, 200) },
      })
    } catch (e) {}
  })
})()
